/**
 * Outils de la barre d'outils du dessin, par groupe, dans l'ordre d'affichage
 * (maquettes V5). Source unique pour la barre (ToolsMenu), sa hauteur (choix
 * 1 ou 2 colonnes) et la modale Affichage (outils affichés ou masqués).
 */
export const TOOLBAR_GROUPS = [
  { id: "selection", tools: ["select", "eraser"] },
  {
    id: "drawing",
    tools: [
      "freehand",
      "text",
      "line",
      "rectangle",
      "ellipse",
      "polyline",
      "dimension",
    ],
  },
  { id: "insertion", tools: ["image"] },
  { id: "view", tools: ["origin", "zoomWindow", "zoomExtents"] },
  { id: "assists", tools: ["objectSnap", "ortho", "polar"] },
] as const;

export type ToolbarGroupId = (typeof TOOLBAR_GROUPS)[number]["id"];
export type ToolbarToolId = (typeof TOOLBAR_GROUPS)[number]["tools"][number];

export const TOOLBAR_TOOL_IDS: readonly ToolbarToolId[] =
  TOOLBAR_GROUPS.flatMap((group) => group.tools);

/** Icône (phosphor) de chaque outil, reprise dans la modale Affichage. */
export const TOOLBAR_TOOL_ICONS: Record<ToolbarToolId, string> = {
  select: "cursor",
  eraser: "eraser",
  freehand: "pencil-simple",
  text: "text-t",
  line: "line-segment",
  rectangle: "square",
  ellipse: "circle",
  polyline: "polygon",
  dimension: "arrows-out-line-horizontal",
  image: "image",
  origin: "ruler",
  zoomWindow: "selection",
  zoomExtents: "magnifying-glass-minus",
  objectSnap: "chart-polar",
  ortho: "vector-two",
  polar: "angle",
};

export interface ToolbarGroup {
  id: ToolbarGroupId;
  tools: ToolbarToolId[];
}

/**
 * Relit la liste des outils masqués (stockage local) : seuls les identifiants
 * connus sont gardés, sans doublon. Un outil renommé ou retiré disparaît.
 */
export function sanitizeHiddenTools(raw: unknown): ToolbarToolId[] {
  if (!Array.isArray(raw)) return [];
  return TOOLBAR_TOOL_IDS.filter((tool) => raw.includes(tool));
}

/** Groupes réduits à leurs outils visibles ; un groupe vide disparaît. */
export function visibleGroups(
  hidden: readonly ToolbarToolId[],
): ToolbarGroup[] {
  return TOOLBAR_GROUPS.map((group) => ({
    id: group.id,
    tools: group.tools.filter((tool) => !hidden.includes(tool)),
  })).filter((group) => group.tools.length > 0);
}
